import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSideBar, SideBarProvider, SideBarPanel, SideBarTrigger, SideBarMenuItem, SideBarGroup } from '../Components/SideBar';
import { useAtom, useSetAtom } from 'jotai';
import { isAuthAtom, authUserAtom, dataUserAtom, darkModeAtom, toggleDarkModeAtom, coinsAtom, setCointAtom, hideHeaderAtom } from '../Services/store/atoms';
import useMediaQuery from '../hooks/useMediaQuery';
import logoHeaderAlone from '../Assets/Images/YOUR-PACK_YP-G-COLOR.webp';
import SideBarDarkModeToggle from '../Components/SideBarDarkModeToggle';
import SideBarVisionModeToggle from '../Components/SideBarVisionModeToggle';
import BellNotifications from '../Components/BellNotifications';
import { PiSignOutLight, PiMagnifyingGlassLight, PiXLight } from 'react-icons/pi';
import { PiCoinsDuotone } from 'react-icons/pi';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import config from '../Config';

// Sin tildes ni mayúsculas, para que «estadisticas» encuentre «Estadísticas».
const normalizeSearch = (text) => (text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

// Deja solo los ítems que contienen todas las palabras buscadas en su título,
// en el nombre del grupo o en sus `keywords`; los grupos vacíos desaparecen.
const filterLinks = (links, query) => {
  const words = normalizeSearch(query).split(/\s+/).filter(Boolean);
  if (!words.length) return links;
  return links
    .map((group) => ({
      ...group,
      items: group.items.filter((link) => {
        const haystack = normalizeSearch(`${link.title} ${group.label || ''} ${link.keywords || ''}`);
        return words.every((word) => haystack.includes(word));
      }),
    }))
    .filter((group) => group.items.length);
};

const LogoutConfirmationModal = ({ show, onConfirm, onCancel, darkMode }) => {
  if (!show) return null;
  return (
    <div className={`fixed inset-0 flex items-center justify-center z-confirm ${darkMode ? 'bg-gray-900/75' : 'bg-white/75'}`}>
      <div className={`p-6 rounded-lg shadow-lg text-center ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'}`}>
        <p className="mb-4">Do you want to close the session?</p>
        <div className="flex justify-center space-x-4">
          <button onClick={onConfirm} className="px-4 py-2 rounded bg-rose-500 hover:bg-rose-600 text-white">
            Close Session
          </button>
          <button onClick={onCancel} className={`px-4 py-2 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-300 hover:bg-gray-400 text-black'}`}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

function DashboardLayoutInner({
  title,
  children,
  links,
  onLinkClick,
  onLogout,
  showLogo = true,
  extraHeaderContent,
  logoutConfirmation,
  sidebarFooter,
  fullScreen = false,
  hideSidebar = false,
  searchable = false,
}) {
  const [darkMode] = useAtom(darkModeAtom);
  const toggleDarkMode = useSetAtom(toggleDarkModeAtom);
  const [hideHeader] = useAtom(hideHeaderAtom);
  const location = useLocation();
  const effectiveFullScreen = fullScreen || location.pathname.includes('/chat');
  const { open } = useSideBar();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [isAuth] = useAtom(isAuthAtom);
  const [authUser] = useAtom(authUserAtom);
  const [dataUser] = useAtom(dataUserAtom);
  const [coins] = useAtom(coinsAtom);
  const setCoint = useSetAtom(setCointAtom);
  const history = useHistory();
  const [linkQuery, setLinkQuery] = useState('');

  // Al navegar se limpia la búsqueda para que el menú vuelva a mostrarse completo.
  useEffect(() => { setLinkQuery(''); }, [location.pathname]);

  useEffect(() => {
    if (isAuth && authUser?.token) {
      axios.defaults.headers.common.Authorization = `Bearer ${authUser.token}`;
      axios.get(`${config.REACT_APP_BASE_URL}/api/v1/coins`)
        .then(response => setCoint(response.data))
        .catch(error => console.error('Error fetching coins:', error));
    }
  }, [isAuth, authUser, setCoint]);

  // Con el sidebar colapsado en escritorio solo se ven íconos: ahí no hay buscador.
  const showLinkSearch = searchable && (open || !isDesktop);
  const visibleLinks = showLinkSearch ? filterLinks(links, linkQuery) : links;

  const handleLinkSearchKeyDown = (e) => {
    if (e.key === 'Escape') setLinkQuery('');
    if (e.key === 'Enter') {
      const first = visibleLinks[0]?.items[0];
      if (first?.url) history.push(first.url);
    }
  };

  // Coincide con el ancho real del panel (17rem abierto, 4.5rem colapsado).
  const sidebarOffset = hideSidebar ? '0' : (isDesktop ? (open ? '17rem' : '4.5rem') : '0');

  const userPhoto = dataUser?.client_profile?.avatar_thumb_url || dataUser?.avatar_thumb_url || dataUser?.client_profile?.avatar || authUser?.avatar || dataUser?.avatar || dataUser?.profile_photo;
  const displayName = dataUser?.nickname || dataUser?.name || dataUser?.email || '?';
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <div className={`ap-app min-h-dvh ${darkMode ? 'dark bg-gray-900 text-white' : 'bg-gray-100 text-black'}`}>
      {/* Top Header Bar (Mobile & Desktop) */}
      {/* El contenido ocupa todo el ancho a la derecha del panel: antes se
          reservaba el ancho del panel también a la derecha y quedaba una
          columna vacía en escritorio. Mismo fondo sólido que el panel lateral
          para que encabezado y panel se vean como una sola pieza. */}
      <header className="sticky top-0 z-[10000] bg-white dark:bg-gray-900 border-b border-black/5 dark:border-white/5 transition-all duration-300" style={{ paddingLeft: sidebarOffset, display: hideHeader ? 'none' : undefined }}>
        <div className="relative flex items-center justify-between h-14 px-4">
          {/* Left: Notifications (extreme) + Coins (inside) - mobile only */}
          <div className="flex items-center gap-2">
            {!isDesktop && <BellNotifications isAuth={isAuth} authUser={authUser} />}
            {!isDesktop && isAuth && (
              <a onClick={() => history.push(`/${dataUser?.role || 'client'}/rechargeCoins`)} className="cursor-pointer flex items-center gap-1 text-rose-500 hover:text-rose-700">
                <PiCoinsDuotone className="w-6 h-6" />
                <span className="text-sm font-bold">{coins}</span>
              </a>
            )}
          </div>

          {/* Center: Logo (mobile only) */}
          {/* aria-label: la marca se lee "YourPack", junta, y no "YOUR Logo PACK". */}
          <a href="/" aria-label="YourPack" className="absolute left-1/2 -translate-x-1/2 cursor-pointer flex items-center shrink-0 font-robotoCondensed" style={{ fontWeight: 900 }}>
            <span aria-hidden="true" className="text-lg text-black dark:text-white mt-0.5" style={{ fontWeight: 900, textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)' }} translate="no">YOUR</span>
            <img src={logoHeaderAlone} width="48" height="48" className="h-12 w-auto -mt-1" alt="" />
            <span aria-hidden="true" className="text-lg text-black dark:text-white mt-0.5" style={{ fontWeight: 900, textShadow: '2px 0px 1px rgba(0, 0, 0, 0.3)' }} translate="no">PACK</span>
          </a>

          {/* Right: Coins (inside) + Notifications (extreme) + Extra actions + Hamburger (mobile) */}
          <div className="flex items-center gap-2">
            {isDesktop && isAuth && (
              <a onClick={() => history.push(`/${dataUser?.role || 'client'}/rechargeCoins`)} className="cursor-pointer flex items-center gap-1 text-rose-500 hover:text-rose-700">
                <PiCoinsDuotone className="w-6 h-6" />
                <span className="text-sm font-bold">{coins}</span>
              </a>
            )}
            {isDesktop && <BellNotifications isAuth={isAuth} authUser={authUser} />}
            {extraHeaderContent}
            {!hideSidebar && <SideBarTrigger />}
          </div>
        </div>
      </header>

      {/* Sidebar */}
      {!hideSidebar && <SideBarPanel
        mobileHeader={
          <div className="flex flex-col w-full gap-3 p-2">
            {isAuth ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="shrink-0">
                    {userPhoto ? (
                      <img src={userPhoto} alt="avatar" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-rose-500 flex items-center justify-center text-white font-bold text-sm">
                        {initial}
                      </div>
                    )}
                  </div>
                  <SideBarDarkModeToggle />
                </div>
                <div className="flex flex-col pl-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{displayName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate capitalize">{dataUser?.role || ''}</p>
                </div>
              </>
            ) : (
              <div className="flex items-center">
                <SideBarDarkModeToggle />
              </div>
            )}
          </div>
        }
        header={
          isAuth ? (
            open ? (
              <div className="flex flex-col w-full gap-3 p-2">
                <div className="flex items-center gap-3">
                  <div className="shrink-0">
                    {userPhoto ? (
                      <img src={userPhoto} alt="avatar" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-rose-500 flex items-center justify-center text-white font-bold text-sm">
                        {initial}
                      </div>
                    )}
                  </div>
                  <SideBarDarkModeToggle />
                </div>
                <div className="flex flex-col pl-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{displayName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate capitalize">{dataUser?.role || ''}</p>
                </div>
              </div>
            ) : (
              <div className="flex justify-center py-2">
                {userPhoto ? (
                  <img src={userPhoto} alt="avatar" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-rose-500 flex items-center justify-center text-white font-bold text-sm">
                    {initial}
                  </div>
                )}
              </div>
            )
          ) : (
            open ? (
              <div className="flex items-center px-4 py-3">
                <SideBarDarkModeToggle />
              </div>
            ) : (
              <div className="flex justify-center py-2">
                <SideBarDarkModeToggle />
              </div>
            )
          )
        }
        footer={sidebarFooter || (
          <div className="space-y-1.5">
            <SideBarVisionModeToggle />
            <SideBarMenuItem
              title="Cerrar Sesión"
              icon={<PiSignOutLight className="text-xl" />}
              onClick={() => logoutConfirmation?.onOpen?.()}
              variant="footer"
              tone="danger"
            />
          </div>
        )}
      >
        {showLinkSearch && (
          <div className="sticky top-0 z-10 px-2 pb-2 bg-white dark:bg-gray-900">
            <div className="relative">
              <PiMagnifyingGlassLight className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" />
              <input
                type="search"
                value={linkQuery}
                onChange={(e) => setLinkQuery(e.target.value)}
                onKeyDown={handleLinkSearchKeyDown}
                placeholder="Buscar en el menú"
                aria-label="Buscar en el menú"
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 py-2 pl-9 pr-8 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-rose-500/40 focus:border-rose-400 [&::-webkit-search-cancel-button]:hidden"
              />
              {linkQuery && (
                <button
                  type="button"
                  onClick={() => setLinkQuery('')}
                  aria-label="Limpiar búsqueda"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <PiXLight />
                </button>
              )}
            </div>
          </div>
        )}
        {showLinkSearch && !visibleLinks.length && (
          <p className="px-4 py-6 text-sm text-center text-gray-400 dark:text-gray-500">Sin resultados para «{linkQuery.trim()}»</p>
        )}
        {(() => {
          // Sin sesión, "Inicio" y "Modelos" van los dos a "/": solo el primer
          // enlace que coincide con la ruta se marca como activo.
          let activeTaken = false;
          const isFirstActive = (url) => {
            if (activeTaken || !url || url !== location.pathname) return false;
            activeTaken = true;
            return true;
          };
          return visibleLinks.map((group, i) => (
          <SideBarGroup key={i} label={group.label}>
            {group.items.map((link, j) => (
              <SideBarMenuItem
                key={j}
                active={isFirstActive(link.url)}
                title={link.title}
                icon={link.icon}
                activeIcon={link.activeIcon}
                url={link.url}
                onClick={link.onClick}
                translate={link.translate}
                badge={link.badge}
                disabled={link.disabled}
              />
            ))}
          </SideBarGroup>
          ));
        })()}
      </SideBarPanel>}

      {/* Main Content */}
      <main className="transition-all duration-300 ease-in-out" style={{ marginLeft: sidebarOffset }}>
        <div className={effectiveFullScreen
          ? 'h-[calc(100dvh-3.5rem)] overflow-hidden'
          : 'min-h-[calc(100dvh-3.5rem)] p-2 lg:p-4'
        }>
          {!effectiveFullScreen && title && (
            <h1 className="ap-large-title mb-5">
              {title}
            </h1>
          )}
          {children}
        </div>
      </main>

      {/* Logout Confirmation Modal */}
      {logoutConfirmation && (
        <LogoutConfirmationModal {...logoutConfirmation} darkMode={darkMode} />
      )}
    </div>
  );
}

function DashboardLayout(props) {
  return (
    <SideBarProvider>
      <DashboardLayoutInner {...props} />
    </SideBarProvider>
  );
}

export default DashboardLayout;
